/**
 * @author NikolaV
 */
import { makeObservable, observable } from 'mobx';
import { Ajax } from '@shared/Ajax';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { Notify } from '@shared/ui-native/Notify';
import { store } from '../store/rootStore';
import { DateUtils } from '@shared/utils/DateUtils';
import { saveAs } from 'file-saver';
import { LStorage } from '@shared/Storage';
import { FilterStore } from '@pages/trucks/store/FilterStore';


export interface ITripStat {
    material: IMaterial;
    volumeUnit: number | string;
    volume: number;
    vehicleId?: number;
    date?: number;
    distance?: number;
    tripTime?: number;
    tripsCount?: number;
    loadAreaId?: number;
    unloadAreaId?: number;
    gpsLoadAreaId?: number;
    gpsUnloadAreaId?: number;
    taskId?: number;
    // set on frontend/fetch
    weekAndYear?: string;
    monthAndYear?: string;
    vehicleTitle?: string;
    loadAreaTitle?: string;
    unloadAreaTitle?: string;
    taskTitle?: string;
    m2?: number;
    tons?: number;
    pieces?: number;
}


export interface ITripStatsSum {
    totalUnits: number;
    totalM2: number;
    totalTons: number;
    totalPieces: number;
    totalDistance: number;
    totalTime: number;
    totalCount: number;
    items: ITripStat[];
}


export type GroupRange = 'day' | 'week' | 'month' | 'all';


export class StatsStore {
    @observable.shallow items: ITripStat[] = [];
    @observable isLoading: boolean = false;

    @observable groupBy: GroupRange = LStorage.getOrDefault('trucks_stats_group_by', 'all');

    activePromise = null;

    constructor(private tripsFilter: FilterStore) {
        makeObservable(this);
    }

    fetch = () => {
        if (this.activePromise) return this.activePromise;
        this.isLoading = true;

        const apiFilter = this.tripsFilter.toApiFilter();
        const convertArraysToStringsExcept = (obj, excludeKeys) => {
            for (const key in obj) {
                if (Array.isArray(obj[key]) && !excludeKeys.includes(key)) {
                    obj[key] = obj[key].join(",");
                }
            }
            return obj;
        };
        const excludeKeys = ["includeEmptyTrips", "distanceFrom", "distanceTo"];
        const convertedData = convertArraysToStringsExcept(apiFilter, excludeKeys);

        this.activePromise = Ajax({
            url: `trucks/stats/daily.json`,
            data: convertedData,
        }).done((resp: any) => {
              const FIELD_EMPTY = -1;
              const FIELD_DELETED = -2;

              const areasIdsSet = new Set([FIELD_EMPTY, ...store.geofences.items.map(g => g.id)]);
              const taskIdsSet = new Set([FIELD_EMPTY, ...store.tasks.items.map(t => t.id)]);

              const areaNames = {
                  [FIELD_EMPTY]: kuura.getMessage('trucks.filter.no_area'),
                  [FIELD_DELETED]: kuura.getMessage('trucks.filter.deleted_area'),
              };

              const taskNames = {
                  [FIELD_EMPTY]: kuura.getMessage('trucks.filter.no_task'),
                  [FIELD_DELETED]: kuura.getMessage('trucks.filter.deleted_task'),
              };

              const vehicleNames = store.vehicles.items.reduce((obj, truck) => {
                  obj[truck.id] = truck.name;
                  return obj;
              }, {});
              store.geofences.items.forEach(a => areaNames[a.id] = a.title);
              store.tasks.items.forEach(t => taskNames[t.id] = t.name);

              resp.stats.reverse().forEach((s: ITripStat) => {
                  // normalize missing areaId, server returns (-1, 0, null)
                  s.loadAreaId = s.loadAreaId > 0 ? s.loadAreaId : FIELD_EMPTY;
                  s.unloadAreaId = s.unloadAreaId > 0 ? s.unloadAreaId : FIELD_EMPTY;
                  s.taskId = s.taskId > 0 ? s.taskId : FIELD_EMPTY;

                  // bad backend fix - areas are hard deleted but trip still keeps deleted area id, so merge all deleted area ids into single value
                  if (s.loadAreaId < 0) {
                      s.loadAreaId = FIELD_EMPTY;
                  } else {
                    if (!areasIdsSet.has(s.loadAreaId)) s.loadAreaId = FIELD_DELETED;
                  }
                  if (s.unloadAreaId < 0) {
                      s.unloadAreaId = FIELD_EMPTY;
                  } else {
                    if (!areasIdsSet.has(s.unloadAreaId)) s.unloadAreaId = FIELD_DELETED;
                  }
                  if (!taskIdsSet.has(s.taskId)) s.taskId = FIELD_DELETED;

                  // pre-calc dates
                  const m = moment(s.date);
                  s.weekAndYear = m.week() + ' ' + m.year();
                  s.monthAndYear = m.format('MMMM YYYY');

                  // set labels
                  s.vehicleTitle = vehicleNames[s.vehicleId];
                  s.loadAreaTitle = areaNames[s.loadAreaId];
                  s.unloadAreaTitle = areaNames[s.unloadAreaId];
                  s.taskTitle = taskNames[s.taskId];

                  // separate volume field to m2/tons/pieces
                  s.m2 = s.volumeUnit == 1 ? s.volume : 0;
                  s.tons = s.volumeUnit == 2 ? s.volume : 0;
                  s.pieces = s.volumeUnit == 'pieces' ? s.volume : 0;
              });

              //this.tripsFilter.setFromStatsData(resp.stats);
              this.items = resp.stats;
          })
          .always(() => {
              this.isLoading = false;
              this.activePromise = null;
          })
          .fail(Notify.error);

        return this.activePromise;
    };

    groupByTimeAndMaterials(items: ITripStat[], groupBy: GroupRange) {
        const groupByTime = _.groupBy(items, s => {
            if (groupBy === 'day') return s.date;
            else if (groupBy === 'week') return s.weekAndYear;
            else if (groupBy === 'month') return s.monthAndYear;
            else if (groupBy === 'all') return 'all';
        });

        const summedGroups: { [key: string]: ITripStatsSum } = {};

        for (const groupKey in groupByTime) {
            summedGroups[groupKey] = this.sumByMaterial(groupByTime[groupKey]);
        }

        return summedGroups as any;
    }

    sumByMaterial(items: ITripStat[]): ITripStatsSum {
        const materialSums: {
            [key: string]: ITripStat
        } = {};

        let totalUnits = 0;
        let totalM2 = 0;
        let totalTons = 0;
        let totalPieces = 0;
        let totalDistance = 0;
        let totalTime = 0;
        let totalCount = 0;

        for (const s of items) {
            const key = s.material.id + '_' + s.volumeUnit;
            if (!materialSums.hasOwnProperty(key)) {
                materialSums[key] = {
                    ...s,
                };
            }
            else {
                materialSums[key].volume += s.volume;
                materialSums[key].m2 += s.m2;
                materialSums[key].tons += s.tons;
                materialSums[key].pieces += s.pieces;

                if (s.distance) {
                    materialSums[key].distance += s.distance;
                }
                if (s.tripTime) {
                    materialSums[key].tripTime += s.tripTime;
                }
                if (s.tripsCount) {
                    materialSums[key].tripsCount += s.tripsCount;
                }
            }

            // calc totals
            totalUnits += s.volume;
            totalM2 += s.m2;
            totalTons += s.tons;
            totalPieces += s.pieces;

            if (s.distance) {
                totalDistance += s.distance;
            }
            if (s.tripTime) {
                totalTime += s.tripTime;
            }
            if (s.tripsCount) {
                totalCount += s.tripsCount;
            }
        }

        const sortedStats = Object.keys(materialSums)
                                  .map(key => materialSums[key])
                                  .sort((s1, s2) => alphaNumSort(s2.material, s1.material));

        return {
            totalUnits: totalUnits || 0,
            totalM2: totalM2 || 0,
            totalTons: totalTons || 0,
            totalPieces: totalPieces || 0,
            totalDistance: totalDistance || 0,
            totalTime: totalTime || 0,
            totalCount: totalCount || 0,
            items: sortedStats || [],
        };
    }

    export = () => {
        const items = this.items;
        const groups = this.groupByTimeAndMaterials(items, this.groupBy);
        let csv = `${this.groupBy !== 'all' ? (this.groupBy+',') : ''}material,m3,tons,pieces,total time,trips,distance (km),average distance\n`;

        Object.entries(groups).forEach(([key, group]) => {
            ((group as any).items as ITripStat[]).forEach(row => {
                const date = this.groupBy === 'day' ? '"' + DateUtils.formatDate(parseInt(key)) + '"': key;

                csv += (this.groupBy !== 'all' ? (date + ',') : '') +
                    row.material.material.replace(',', ' ') + ',' +
                    (row.m2 > 0 ? parseFloat(row.m2.toFixed(2)) : '') + ',' +
                    (row.tons > 0 ? parseFloat(row.tons.toFixed(2)) : '') + ',' +
                    (row.pieces > 0 ? parseFloat(row.pieces.toFixed(2)) : '') + ',' +
                    (DateUtils.secondsToDateTime(row.tripTime)) + ',' +
                    row.tripsCount + ',' +
                    (parseFloat((row.distance / 1000).toFixed(2))) + ',' +
                    (parseFloat(((row.distance / row.tripsCount) / 1000).toFixed(2))) + '\n';
            });
        });

        const file = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(file, kuura.getProjectName() + '_truck_stats.csv');
    };

    setGroupBy = (val: GroupRange) => {
        this.groupBy = val;
        LStorage.setItem('trucks_stats_group_by', val);
    }
}

